interface MyStudiesRouteNames {
  readonly Root: string;
  readonly StudiesSelection: string;
}

interface DashboardRouteNames {
  readonly Root: string;
  readonly Home: string;
  readonly MyInterviews: string;
  readonly MyStudies: MyStudiesRouteNames;
  readonly PersonalDataEdition: string;
  readonly Invoices: string;
}

interface HomeRouteNames {
  readonly Root: string;
  readonly Contact: string;
  readonly RulesWarsaw: string;
  readonly RulesGdansk: string;
  readonly RulesBytom: string;
}

interface AppRouteNames {
  readonly Root: string;
  readonly InitialWizard: string;
  readonly Dashboard: DashboardRouteNames;
}

interface RouteNamesType {
  readonly Home: HomeRouteNames;
  readonly App: AppRouteNames;
}

export const AppRouteNames: RouteNamesType = {
  Home: {
    Root: '',
    Contact: 'contact',
    RulesWarsaw: 'rules-warsaw',
    RulesGdansk: 'rules-gdansk',
    RulesBytom: 'rules-bytom'
  },
  App: {
    Root: 'app',
    InitialWizard: 'initial-wizard',
    Dashboard: {
      Root: 'dashboard',
      Home: 'home',
      MyInterviews: 'my-interviews',
      MyStudies: {
        Root: 'my-studies',
        StudiesSelection: 'studies-selection'
      },
      PersonalDataEdition: 'personal-data-edition',
      Invoices: 'invoices'
    }
  }
};




interface PageData {
  Title: string; // page's title (browser's tab title)
  Header: string;
  Breadcrumb?: string;
  Other?: any;
}

export interface RouteData {
  readonly StringPath: string;
  readonly ArrayPath: string[];
  readonly PageData: PageData;
}

interface MyStudiesRouteData {
  readonly Root: RouteData;
  readonly StudiesSelection: RouteData;
}

interface DashboardRouteData {
  readonly Home: RouteData;
  readonly MyInterviews: RouteData;
  readonly MyStudies: MyStudiesRouteData;
  readonly PersonalDataEdition: RouteData;
  readonly Invoices: RouteData;
}

interface AuthRouteData {
  readonly Register: RouteData;
  readonly Login: RouteData;
  readonly Logout: RouteData;
}

interface HomeRouteData {
  readonly Root: RouteData;
  readonly Contact: RouteData;
  readonly RulesWarsaw: RouteData;
  readonly RulesGdansk: RouteData;
  readonly RulesBytom: RouteData;
}

interface AppRouteData {
  readonly InitialWizard: RouteData;
  readonly Dashboard: DashboardRouteData;
}

export interface AppPaths {
  readonly Auth: AuthRouteData;
  readonly Home: HomeRouteData;
  readonly App: AppRouteData;
}

let applicationPaths: AppPaths = {
  Auth: {
    Register: {
      StringPath: 'defined-in-separate-file__api-authorization.constants.ts',
      ArrayPath: [],
      PageData: {
        Title: 'SHARED.REGISTER',
        Header: null
      }
    },
    Login: {
      StringPath: 'defined-in-separate-file__api-authorization.constants.ts',
      ArrayPath: [],
      PageData: {
        Title: 'SHARED.LOGIN.LOG-IN',
        Header: null
      }
    },
    Logout: {
      StringPath: 'defined-in-separate-file__api-authorization.constants.ts',
      ArrayPath: [],
      PageData: {
        Title: 'SHARED.LOGOUT.WAIT-FOR-LOGOUT',
        Header: null
      }
    }
  },
  Home: {
    Root: {
      StringPath: `${AppRouteNames.Home.Root === '' ? '' : '/'}${AppRouteNames.Home.Root}`, // this fixes bug with redirect to / when root page is ''
      ArrayPath: [],
      PageData: {
        Title: 'HOME.ROOT.PAGE-TITLE',
        Header: 'HOME.ROOT.HEADER'
      }
    },
    Contact: {
      StringPath: `/${AppRouteNames.Home.Contact}`,
      ArrayPath: [],
      PageData: {
        Title: 'HOME.CONTACT.PAGE-TITLE',
        Header: 'HOME.CONTACT.HEADER'
      }
    },
    RulesWarsaw: {
      StringPath: `/${AppRouteNames.Home.RulesWarsaw}`,
      ArrayPath: [],
      PageData: {
        Title: 'HOME.RULES-WARSAW.PAGE-TITLE',
        Header: 'HOME.RULES-WARSAW.HEADER'
      }
    },
    RulesGdansk: {
      StringPath: `/${AppRouteNames.Home.RulesGdansk}`,
      ArrayPath: [],
      PageData: {
        Title: 'HOME.RULES-GDANSK.PAGE-TITLE',
        Header: 'HOME.RULES-GDANSK.HEADER'
      }
    },
    RulesBytom: {
      StringPath: `/${AppRouteNames.Home.RulesBytom}`,
      ArrayPath: [],
      PageData: {
        Title: 'HOME.RULES-BYTOM.PAGE-TITLE',
        Header: 'HOME.RULES-BYTOM.HEADER'
      }
    }
  },
  App: {
    InitialWizard: {
      StringPath: `/${AppRouteNames.App.Root}/${AppRouteNames.App.InitialWizard}`,
      ArrayPath: [],
      PageData: {
        Title: 'INITIAL-WIZARD.PAGE-TITLE',
        Header: 'INITIAL-WIZARD.HEADER',
        Breadcrumb: 'INITIAL-WIZARD.BREADCRUMB'
      }
    },
    Dashboard: {
      Home: {
        StringPath: `/${AppRouteNames.App.Root}/${AppRouteNames.App.Dashboard.Root}/${AppRouteNames.App.Dashboard.MyStudies.Root}`,
        ArrayPath: [],
        PageData: {
          Title: 'DASHBOARD.MY-STUDIES.PAGE-TITLE',
          Header: 'DASHBOARD.MY-STUDIES.HEADER',
          Breadcrumb: 'DASHBOARD.MY-STUDIES.BREADCRUMB',
          Other: { MenuItemTitle: 'DASHBOARD.HOME.MENU-ITEM-TITLE' }
        }
      },
      MyInterviews: {
        StringPath: `/${AppRouteNames.App.Root}/${AppRouteNames.App.Dashboard.Root}/${AppRouteNames.App.Dashboard.MyInterviews}`,
        ArrayPath: [],
        PageData: {
          Title: 'DASHBOARD.MY-INTERVIEWS.PAGE-TITLE',
          Header: 'DASHBOARD.MY-INTERVIEWS.HEADER',
          Breadcrumb: 'DASHBOARD.MY-INTERVIEWS.BREADCRUMB'
        }
      },
      MyStudies: {
        Root: {
          StringPath: `/${AppRouteNames.App.Root}/${AppRouteNames.App.Dashboard.Root}/${AppRouteNames.App.Dashboard.MyStudies.Root}`,
          ArrayPath: [],
          PageData: {
            Title: 'DASHBOARD.MY-STUDIES.PAGE-TITLE',
            Header: 'DASHBOARD.MY-STUDIES.HEADER',
            Breadcrumb: 'DASHBOARD.MY-STUDIES.BREADCRUMB'
          }
        },
        StudiesSelection: {
          StringPath: `/${AppRouteNames.App.Root}/${AppRouteNames.App.Dashboard.Root}/${AppRouteNames.App.Dashboard.MyStudies.Root}/${AppRouteNames.App.Dashboard.MyStudies.StudiesSelection}`,
          ArrayPath: [],
          PageData: {
            Title: 'DASHBOARD.MY-STUDIES.STUDIES-SELECTION.PAGE-TITLE',
            Header: 'DASHBOARD.MY-STUDIES.STUDIES-SELECTION.HEADER',
            Breadcrumb: 'DASHBOARD.MY-STUDIES.STUDIES-SELECTION.BREADCRUMB'
          }
        },
      },
      PersonalDataEdition: {
        StringPath: `/${AppRouteNames.App.Root}/${AppRouteNames.App.Dashboard.Root}/${AppRouteNames.App.Dashboard.PersonalDataEdition}`,
        ArrayPath: [],
        PageData: {
          Title: 'DASHBOARD.DATA-EDITION.PAGE-TITLE',
          Header: 'DASHBOARD.DATA-EDITION.HEADER',
          Breadcrumb: 'DASHBOARD.DATA-EDITION.BREADCRUMB'
        }
      },
      Invoices: {
        StringPath: `/${AppRouteNames.App.Root}/${AppRouteNames.App.Dashboard.Root}/${AppRouteNames.App.Dashboard.Invoices}`,
        ArrayPath: [],
        PageData: {
          Title: 'DASHBOARD.INVOICES.PAGE-TITLE',
          Header: 'DASHBOARD.INVOICES.HEADER',
          Breadcrumb: 'DASHBOARD.INVOICES.BREADCRUMB'
        }
      }
    }
  }
};

applicationPaths = {
  ...applicationPaths,
  Home: {
    ...applicationPaths.Home,
    Root: {
      ...applicationPaths.Home.Root,
      ArrayPath: applicationPaths.Home.Root.StringPath.split('/')
    },
    Contact: {
      ...applicationPaths.Home.Contact,
      ArrayPath: applicationPaths.Home.Contact.StringPath.split('/')
    },
    RulesWarsaw: {
      ...applicationPaths.Home.RulesWarsaw,
      ArrayPath: applicationPaths.Home.RulesWarsaw.StringPath.split('/')
    },
    RulesGdansk: {
      ...applicationPaths.Home.RulesGdansk,
      ArrayPath: applicationPaths.Home.RulesGdansk.StringPath.split('/')
    },
    RulesBytom: {
      ...applicationPaths.Home.RulesBytom,
      ArrayPath: applicationPaths.Home.RulesBytom.StringPath.split('/')
    }
  },
  App: {
    ...applicationPaths.App,
    InitialWizard: {
      ...applicationPaths.App.InitialWizard,
      ArrayPath: applicationPaths.App.InitialWizard.StringPath.split('/')
    },
    Dashboard: {
      ...applicationPaths.App.Dashboard,
      Home: {
        ...applicationPaths.App.Dashboard.Home,
        ArrayPath: applicationPaths.App.Dashboard.Home.StringPath.split('/')
      },
      MyInterviews: {
        ...applicationPaths.App.Dashboard.MyInterviews,
        ArrayPath: applicationPaths.App.Dashboard.MyInterviews.StringPath.split('/')
      },
      MyStudies: {
        ...applicationPaths.App.Dashboard.MyStudies.Root,
        Root: {
          ...applicationPaths.App.Dashboard.MyStudies.Root,
          ArrayPath: applicationPaths.App.Dashboard.MyStudies.Root.StringPath.split('/')
        },
        StudiesSelection: {
          ...applicationPaths.App.Dashboard.MyStudies.StudiesSelection,
          ArrayPath: applicationPaths.App.Dashboard.MyStudies.StudiesSelection.StringPath.split('/')
        },
      },
      PersonalDataEdition: {
        ...applicationPaths.App.Dashboard.PersonalDataEdition,
        ArrayPath: applicationPaths.App.Dashboard.PersonalDataEdition.StringPath.split('/')
      },
      Invoices: {
        ...applicationPaths.App.Dashboard.Invoices,
        ArrayPath: applicationPaths.App.Dashboard.Invoices.StringPath.split('/')
      }
    }
  }
};

export const AppPaths: AppPaths = applicationPaths;
